import React from "react";
import Overlay from "../Overlay";
import SimpleTable from "../tables/SimpleTable";
import FilterCard, { FIELD_TYPE } from "../FilterCard";
import {
  getBoolSelectVal,
  hideModal,
  showModalNoOutsideClick,
} from "../../util/FormatUtil";
import { sweetalert } from "../../App";
import NetworkUtil from "../../network/NetworkUtil";
import { Column } from "../tables/TableBase";
import { Sorter, TableOrder } from "../../sorting/Sorter";
import SystemAPI from "../../network/SystemAPI";
import { AdminPages } from "./AdminNav";
import { SpecimenSource } from "../../types/SpecimenSource";
import SpecimenSourceAPI from "../../network/SpecimenSourceAPI";
import { SpecimenSourceManagementModal } from "../modals/SpecimenSourceManagementModal";
import { fetchFieldPermissions } from "../../util/FieldPermissionsFetcher";

interface SpecimenSourceManagementState {
  showLoading: boolean;
  selectedSpecimenSource: SpecimenSource;
  specimenSourceQueryResults;
  tableData: {}[];
  filter?: {
    SpecimenSource;
    Description;
    Test;
    IsEnabled;
    StarlimsName;
  };
  direction?: TableOrder;
  specimenSourceList: { label; value }[];
  descList: { label; value }[];
  testsList: { label; value }[];
  starlimsList: { label; value }[];
  pageCustomQuestions?
  userRoles?: string;
  fieldPermissions?: Record<string, number[]>;
}

export class SpecimenSourceManagement extends React.Component<
  any,
  SpecimenSourceManagementState
> {
  
  constructor(props) {
    super(props);

    this.state = {
      showLoading: false,
      selectedSpecimenSource: {} as SpecimenSource,
      tableData: [] as any,
      specimenSourceQueryResults: [] as any,
      filter: { SpecimenSource: [], Description: [], Test: [], IsEnabled: [], StarlimsName: []},
      direction: "asc",
      specimenSourceList: [],
      descList: [],
      testsList: [],
      starlimsList:[],
      pageCustomQuestions: [],
    };

    this.createOrModifySpecimenSources = this.createOrModifySpecimenSources.bind(this);
  }

  componentDidMount() {
    document.title = 'Specimen Source Management Page';
    fetchFieldPermissions((state) => this.setState(state as any));
    this.loadSpecimenSourceData();
    SystemAPI.getCustomQuestions(AdminPages.SpecimenSource).then(res =>{
      this.setState({pageCustomQuestions:res.questions})
  })
  }

  loadSpecimenSourceData() {
    this.setState({ showLoading: true }, async () => {
        SpecimenSourceAPI.getAllSpecimenSourceData()
        .then((response) => {
            let specimenSourceList = response.data.specimenSources
              .map(specimenSource => ({label: specimenSource.Name, value: specimenSource.ID}))
              .sort((a, b) => a.label.localeCompare(b.label))
            
            let descList = response.data.specimenSources
              .map(specimenSource => ({label: specimenSource.Description, value: specimenSource.Description}))
              .sort((a, b) => a.label.localeCompare(b.label))
            
            let starlimsList = response.data.specimenSources
              .map(specimenSource => ({label: specimenSource.StarlimsName, value: specimenSource.StarlimsName }))
              .sort((a, b) => a.label.localeCompare(b.label))

            let testsList = response.data.tests
              .map(test => ({label: test.Name, value: test.Name}))
              .sort((a, b) => a.label.localeCompare(b.label))

          this.setState({ 
            specimenSourceList: specimenSourceList, 
            descList: descList, testsList: testsList, 
            starlimsList: starlimsList, 
            showLoading: false 
          });
        })
        .catch((e) => {
          console.error(e);
          return sweetalert.fire({
            icon: "info",
            title: "",
            text: "No Specimen Sources found.",
          });
        });
    });
    this.querySpecimenSourceData()
  }

  querySpecimenSourceData() {
    this.setState({ showLoading: true }, async () => {
      let specimenSources = await SpecimenSourceAPI.filterSpecimenSources({
        filter: {
          SpecimenSource: this.state.filter.SpecimenSource,
          Description: this.state.filter.Description,
          Test: this.state.filter.Test,
          IsEnabled: this.state.filter.IsEnabled,
          StarlimsName: this.state.filter.StarlimsName,
        },
      });
      if(specimenSources.data[0].length < 1){
        this.setState({showLoading: false, tableData: [], specimenSourceQueryResults: null})
        return sweetalert.fire({title: '', text: 'No Specimen Sources returned', icon: 'info'})
      }
      if (!specimenSources.success) {
        return sweetalert.fire({
          title: "",
          text: "Unable to filter Specimen Source data at this time",
          icon: "error",
        });
      }
      this.setState({
        tableData: specimenSources.data[0],
        specimenSourceQueryResults: specimenSources.data,
        showLoading: false,
      });
    });
  }

  createOrModifySpecimenSources(specimenSource: SpecimenSource) {
    this.setState({ showLoading: true }, async () => {
      if (specimenSource.ID === null) {
        let response = await SpecimenSourceAPI.createSpecimenSource(specimenSource);
        if (!response.success) {
          let msg = "Unable to create Specimen Source at this time.";
          if (response.reason) {
            msg = response.reason;
          }
          return sweetalert.fire({
            icon: "error",
            title: "",
            text: msg,
            allowOutsideClick: false
          }).then((response) => {
            if(response.isConfirmed){
              window.location.reload()
            }
          });
        }
      } else {
        let response = await SpecimenSourceAPI.editSpecimenSource(specimenSource);
        if (!response.success) {
          let msg = "Unable to edit Specimen Source at this time.";
          if (response.reason) {
            msg = response.reason;
          }
          return sweetalert.fire({
            icon: "error",
            title: "",
            text: msg,
            allowOutsideClick: false
          }).then((response) => {
            if(response.isConfirmed){
              window.location.reload()
            }
          });
        }
      }

      sweetalert
        .fire({ icon: "success", title: "", text: "Specimen Source saved" })
        .then(() => {
          this.setState(
            {
              selectedSpecimenSource: {
                ID: null,
                Name: null,
                Description: null,
                StarlimsName: null,
                CustomFieldData: null,
                CreatedBy: null,
                CreatedDate: null,
                UpdatedBy: null,
                UpdatedDate: null
              } as SpecimenSource,
            },
            () => {
              this.loadSpecimenSourceData();
              this.querySpecimenSourceData();
            }
          );
        });
    });
  }

  handleExportToCSV() {
    this.setState({ showLoading: true }, async () => {
      await NetworkUtil.downloadCSV(
        "/api/admin/specimenSource/csv",
        "Specimen Source Management Data.xlsx",
        { filter: this.state.filter });
      this.setState({ showLoading: false });
    });
  }

  renderSpecimenSourceFilterFields() {
    return (
      <FilterCard
        fields={[
          {
            label: "Specimen Source",
            key: "SpecimenSource",
            type: FIELD_TYPE.SELECT,
            options: this.state.specimenSourceList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true,
            popoverText: "Name of Specimen Source"
          },
          {
            label: "Description",
            key: "Description",
            type: FIELD_TYPE.SELECT,
            options: this.state.descList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true,
            popoverText: "Short Description of Specimen Source"
          },
          {
            label: "Starlims Name",
            key: "StarlimsName",
            type: FIELD_TYPE.SELECT,
            options: this.state.starlimsList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true,
            popoverText: "Mapping for Starlims POST"
          },
          {
            label: "Test",
            key: "Test",
            type: FIELD_TYPE.SELECT,
            options: this.state.testsList,
            isMapped: true,
            isMulti: true,
            textType: "text",
            isFilter: true,
            popoverText: "Tests associated with Specimen Source"
          },
          {
            label: "Is Enabled",
            key: "IsEnabled",
            type: FIELD_TYPE.SELECT,
            options: [{label: 'Yes', value: [1]}, {label: 'No', value: [0]}],
            isMapped: true,
            textType: "text",
            isClearable: true,
            isFilter: true,
            popoverText: "Enabled Specimen Sources can be assigned to Tests?"
          }
        ]}
        filterChanged={(e) => this.setState({ filter: e })}
      />
    );
  }

  useSorter(col: Column) {
    let sorter = new Sorter<any>();

    this.setState({
      tableData: sorter.sortByKey(
        this.state.tableData,
        col.key as keyof any,
        this.state.direction
      ),
      direction: this.state.direction === "asc" ? "desc" : "asc",
    });
  }

  render() {
    // console.log('Specimen Source Mgmt STATE', this.state);
    return (
      <React.Fragment>
        <Overlay show_loading={this.state.showLoading} />
        <SpecimenSourceManagementModal
          pageCustomQuestions={this.state.pageCustomQuestions}
          selectedSpecimenSource={this.state.selectedSpecimenSource}
          onSubmit={(specimenSource) => {
            hideModal(SpecimenSourceManagementModal.ID);
            this.createOrModifySpecimenSources(specimenSource);
          }}
          userRoles={this.state.userRoles}
          fieldPermissions={this.state.fieldPermissions}
        />

        <div className="container-fluid ">
          <div className={"row"}>
            <div className="col-12 col-md-12 col-lg-8 col-xl-5 pt-2">
              <main id="main-content" tabIndex={-1} aria-label="Specimen Source Management">
                <div className="card mb-2">
                  <div className="card-header verlag-bold">
                    <h3>Specimen Source Management</h3>
                  </div>
                  <div className="card-body">
                    {this.renderSpecimenSourceFilterFields()}
                  </div>
                  <div className="card-footer">
                    <button
                      className={"btn immySubmitButtonOutline"}
                      onClick={() => this.querySpecimenSourceData()}
                    >
                      Search
                    </button>
                    <button
                      className="btn btn-outline-success float-right"
                      onClick={() =>
                        this.setState(
                          {
                            selectedSpecimenSource: {
                              ID: null,
                              Name: "",
                              Description: "",
                              CustomFieldData: null,
                            } as SpecimenSource,
                          },
                          () => {
                            showModalNoOutsideClick(SpecimenSourceManagementModal.ID);
                          }
                        )
                      }
                    >
                      Create New
                    </button>
                  </div>
                </div>
              </main>
            </div>
            {this.state.tableData && this.state.tableData.length > 0 && (
              <div className="col-12 col-md-12 col-lg-12 pt-2">
                <div className="card mt-2" style={{marginBottom: '4rem'}}>
                  <div className="card-header verlag-bold stickToTop">
                    <h4 className="text-center text-md-left">
                      Specimen Sources
                      <section className="tableHeaderSection float-md-right d-flex justify-content-around">
                        <h4 className={"float-md-right"} aria-label="Total Records" role="alert">
                          Total: {(this.state.specimenSourceQueryResults[0]).length }
                        </h4>
                      </section>
                      <button
                        className={"d-none d-md-inline btn btn-outline-primary ml-3"}
                        onClick={() => this.handleExportToCSV()}
                      >
                        Export to CSV
                      </button>
                    </h4>
                  </div>
                  <div className="card-body p-0 m-0 table-responsive">
                    <SimpleTable
                      columns={[
                        {
                          label: "Specimen Source",
                          key: "Name",
                          popoverText: "Click to Sort By Specimen Source",
                          rawFormat: (val) => {
                            return (
                              <a
                                href={"#"}
                                className={'tableNameLinkColor'}
                                onClick={() => {
                                  this.setState({ selectedSpecimenSource: val }, () =>
                                    showModalNoOutsideClick(
                                      SpecimenSourceManagementModal.ID
                                    )
                                  );
                                }}
                              >
                                {val.Name}
                              </a>
                            );
                          },
                        },
                        {
                          label: "Description",
                          key: "Description",
                          popoverText: "Click to Sort By Description",
                        },
                        {
                          label: "Starlims Name",
                          key: "StarlimsName",
                          popoverText: "Click to Sort By Starlims Name",
                        },
                        {
                          label: "LOINC Code",
                          key: "LOINCCode",
                          popoverText: "Click to Sort By LOINC Code",
                        },
                        {
                          label: "LOINC Description",
                          key: "LOINCDescription",
                          popoverText: "Click to Sort By LOINC Description",
                        },
                        {
                          label: "Tests",
                          key: "TestName",
                          popoverText: "Click to Sort By Tests",
                          rawFormat: (val) => {
                            return <p className="testsString">{val.TestName ? val.TestName : 'Not Assigned'}</p>;
                          }
                        },
                        {
                          label: "Is Enabled?",
                          key: "IsEnabled",
                          popoverText: "Click to Sort By Is Enabled",
                          rawFormat: (val) => {
                            let string = getBoolSelectVal(val.IsEnabled);
                            return <p>{string.label}</p>;
                          },
                        }
                      ]}
                      table_data={this.state.tableData}
                      columnClickedCallback={(col) => {
                        this.useSorter(col);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}